import { CartItem, IProductCart } from "@/types/app";
import { create } from "zustand";

interface Cart {
    productos: IProductCart[];
    total: number;
    agregarProducto: (producto: IProductCart) => void;
    eliminarProducto: (producto: IProductCart) => void;
    vaciarCarrito: () => Promise<void>;
    actualizarProducto: (producto: IProductCart, nuevaCantidad: number) => void;
    setCart: (cart: CartItem[]) => void;
    actualizarProductoCupon: (producto: IProductCart) => void;
    actualizarCantidad: (rowId: string, nuevaCantidad: number) => void;
    sincronizarConBackend: () => Promise<void>;
}

export const useCartStore = create<Cart>()((set) => ({
    productos: [],
    total: 0,
    setCart: (cart) => {
        const productos = cart.map((p) => ({
            id: p.id,
            nombre: p.name,
            img: p.options.imagen,
            sabor: p.options.sabor,
            unidades: p.qty,
            personas: p.options.cantidad,
            reserva: p.options.fechaReserva,
            valor: p.price,
            mensaje: p.options.mensaje,
            producto_es_listable: p.options.producto_es_listable,
            descuento: p.options.descuento,
            edad: p.options.edad,
            sexo: p.options.sexo,
            imagen_personalizada: p.options.imagen_personalizada,
            es_personalizada: p.options.es_personalizada,
            sabor_id: p.options.sabor_id,
            cantidad_id: p.options.cantidad_id,
            row_id: p.rowId,
            stock: p.options.stock,
            tematica: p.options.tematica,
            tipo: p.options.tipo,
            tieneDecoracionExtra: p.options.tieneDecoracionExtra,
            decoracionExtraSeleccionada: p.options.decoracionExtraSeleccionada,
            esCupon: p.options.esCupon,
            tipo_despacho: p.options.tipo_despacho,
        }));
        const total = productos.reduce(
            (acc, p) => acc + (p.valor || 0) * (p.unidades || 0),
            0
        );
        set({ productos, total });
    },
    agregarProducto: (producto: IProductCart) =>
        set((state) => {
            const productoIndex = state.productos.findIndex(
                (p) => p.row_id === producto.row_id
            );
            if (productoIndex !== -1) {
                const productos = [...state.productos];
                productos[productoIndex].unidades = producto.unidades;
                const calcularTotal = productos.reduce(
                    (acc, p) => acc + (p.valor || 0) * (p.unidades || 0),
                    0
                );
                return {
                    productos: productos,
                    total: calcularTotal,
                };
            } else {
                const productos = [...state.productos, producto];
                const calcularTotal = productos.reduce(
                    (acc, p) => acc + (p.valor || 0) * (p.unidades || 0),
                    0
                );
                return {
                    productos,
                    total: calcularTotal,
                };
            }
        }),
    eliminarProducto: (producto: IProductCart) =>
        set((state) => {
            const productos = [...state.productos];
            const productosFiltrados = productos.filter(
                (p) => p.row_id !== producto.row_id
            );

            const calcularTotal = productosFiltrados.reduce(
                (acc, p) => acc + (p.valor || 0) * (p.unidades || 0),
                0
            );
            return {
                productos: productosFiltrados,
                total: calcularTotal,
            };
        }),
    vaciarCarrito: async () => {
        try {
            const response = await fetch(`${window.origin}/api/cart/destroy`);
            if (response.ok) {
                set({
                    productos: [],
                    total: 0,
                });
            } else {
                console.error('Error al vaciar el carrito en el backend');
            }
        } catch (error) {
            console.error('Error al vaciar el carrito:', error);
        }
    },
    actualizarProducto: (
        producto: IProductCart,
        nuevaCantidad: number //nueva cantidad should be the new quantity of the product
    ) =>
        set((state) => {
            const productoIndex = state.productos.findIndex(
                (p) => p.id === producto.id
            );
            if (productoIndex === -1) return state;
            const productos = [...state.productos];

            productos[productoIndex] = {
                ...producto,
                unidades: nuevaCantidad,
            };

            const total = productos.reduce(
                (acc, p) => acc + (p.valor || 0) * (p.unidades || 0),
                0
            );

            return {
                productos,
                total: total,
            };
        }),
    actualizarProductoCupon: (producto: IProductCart) => set((state) => {
        //revisar si existe un producto que sea cupon y eliminarlo
        const productosSinCupon = state.productos.filter((p) => !p.esCupon);
        //agregar el nuevo producto
        const productos = [...productosSinCupon, producto];
        const total = productos.reduce(
            (acc, p) => acc + (p.valor || 0) * (p.unidades || 0),
            0
        );
        return {
            productos,
            total,
        };
    }),
    actualizarCantidad: (rowId: string, nuevaCantidad: number) =>
        set((state) => {
            const productoIndex = state.productos.findIndex(
                (p) => p.row_id === rowId
            );
            if (productoIndex === -1) return state;
            
            const productos = [...state.productos];
            productos[productoIndex].unidades = nuevaCantidad;
            
            const total = productos.reduce(
                (acc, p) => acc + (p.valor || 0) * (p.unidades || 0),
                0
            );
            
            return {
                productos,
                total,
            };
        }),
    sincronizarConBackend: async () => {
        try {
            const response = await fetch(`${window.origin}/api/cart/list`);
            if (response.ok) {
                const cartData = await response.json();
                set({ productos: cartData, total: 0 });
            }
        } catch (error) {
            console.error('Error syncing cart with backend:', error);
        }
    },
}));
